import { matchPath, useLocation } from "react-router";
import { Membership, ProductFeature, Sangha, SanghaType } from "@app/shared/types";
import { useSelector } from "react-redux";
import { NOTION_FAQ_PAGE } from "features/pages/FAQPage";
import {
    selectCanAccessMemberZone,
    selectIsLoggedIn,
    selectIsTeacher,
    selectUser,
} from "features/auth/auth";
import { routes } from "../../app/routes";
import { isFeatureEnabled, SiteFeature } from "app/featureFlags";
import { useLazyQuery, useQuery } from "@apollo/client";
import { GRAPHQL_QUERY_MEMBERSHIPS, GRAPHQL_QUERY_MY_SANGHAS } from "app/queries";
import { useEffect } from "react";
import { MembershipStatus } from "@app/shared/constants";
import { membershipUsesFreeTrialInternally } from "features/member/membership/membershipHelpers";
const TEACHER_PORTAL_LINK =
    "https://www.notion.so/cloudsangha/Cloud-Sangha-Teacher-Portal-55811247c7ee4698a198515e8a579f90";

export enum LinkTag {
    LOGIN = "LOGIN",
    LOGOUT = "LOGOUT",
    ACCOUNT_LINKS = "ACCOUNT_LINKS",
}

export interface MenuLinkInfo {
    name: string;
    to: string;
    external?: boolean;
    hashLink?: boolean;
    testId?: string;
    tags: LinkTag[];
    isAnchorLink?: boolean;
    indicatorKey?: string;
    subNavLinks?: MenuLinkInfo[];
}

const extractValidSanghaId = (sanghaId: string) => {
    if (sanghaId === "invitation" || sanghaId === "apply") {
        return null;
    }
    return sanghaId;
};

const getSanghaIdFromRoute = (path: string): string | null => {
    const match = matchPath<{ sanghaId: string }>(path, routes.memberSanghaDetails(":sanghaId"));
    return match ? extractValidSanghaId(match.params.sanghaId) : null;
};

// Location Identifiers

const isMemberZoneRoot = (path: string): boolean => {
    const match = matchPath(path, {
        path: routes.memberZone(),
        exact: true,
    });
    return Boolean(match);
};

// Links

const aboutUsLink = (): MenuLinkInfo => ({
    name: "About us",
    to: routes.about(),
    tags: [],
});

const visitorDonateLink = (): MenuLinkInfo => ({
    name: "Donate",
    to: routes.visitorDonate(),
    tags: [],
});

const contentLibraryLink = (): MenuLinkInfo => ({
    name: "Recorded Content Library",
    to: "https://cloudsangha.notion.site/d6b24ef7ceac4579959a172d905ab264?v=28bf310df5c542a5b951d989c2644072",
    external: true,
    tags: [],
});

const accountLink = (): MenuLinkInfo => ({
    name: "Account settings",
    to: routes.memberMembership(),
    tags: [LinkTag.ACCOUNT_LINKS],
});

const contactUsLink = (): MenuLinkInfo => ({
    name: "Contact us",
    to: routes.contactUs(),
    tags: [],
});

const faqLink = (): MenuLinkInfo => {
    return {
        name: "Frequently Asked Questions",
        to: NOTION_FAQ_PAGE,
        external: true,
        tags: [],
    };
};

const homeLink = (): MenuLinkInfo => ({
    name: "Home",
    to: routes.home(),
    tags: [],
});

const memberHomeLink = (): MenuLinkInfo => ({
    name: "Home",
    to: routes.memberHomePage(),
    testId: "memberHomeNavLink",
    tags: [],
});

const logInOutButton = (isLogout: boolean): MenuLinkInfo => ({
    name: isLogout ? "Log out" : "Login",
    to: routes.memberZone(),
    testId: isLogout ? "logoutNavLink" : "loginNavLink",
    tags: isLogout ? [LinkTag.LOGOUT, LinkTag.ACCOUNT_LINKS] : [LinkTag.LOGIN],
});

const memberDropInSessionsTalksLink = (): MenuLinkInfo => ({
    name: "Talks",
    testId: "dropInSessionsTalksNavLink",
    to: routes.memberDropInSessionsTalks(),
    tags: [],
});

const memberDropInSessionsMonthlyThemeLink = (): MenuLinkInfo => ({
    name: "This Month's Theme",
    testId: "dropInSessionsMonthlyThemeNavLink",
    to: routes.memberDropInSessionsMonthlyTheme(),
    tags: [],
});

const memberDropInSessionsMeditationsDiscussionsLink = (): MenuLinkInfo => ({
    name: "Meditations",
    testId: "dropInSessionsMeditationsDiscussionsNavLink",
    to: routes.memberDropInSessionsMeditationsDiscussions(),
    tags: [],
});

const memberDropInSessionsWelcomeSessionsLink = (): MenuLinkInfo => ({
    name: "Welcome & Orientation Sessions",
    testId: "dropInSessionsWelcomeSessionsNavLink",
    to: routes.memberDropInSessionsWelcomeSessions(),
    tags: [],
});

const memberDropInSessionsAllSessionsLink = (): MenuLinkInfo => ({
    name: "All Sessions",
    testId: "dropInSessionsAllSessionsNavLink",
    to: routes.memberDropInSessionsAllSessions(),
    tags: [],
});

const memberDropInSessionsSilentMeditationsLink = (): MenuLinkInfo => ({
    name: "Silent Meditations",
    testId: "dropInSessionsSilentMeditationsNavLink",
    to: routes.memberDropInSessionsSilentMeditations(),
    tags: [],
});

const memberDropInSessionsLink = (): MenuLinkInfo => ({
    name: "Drop-In Sessions",
    testId: "dropInSessionsNavLink",
    to: routes.memberDropInSessionsMeditationsDiscussions(),
    tags: [],
    subNavLinks: [
        memberDropInSessionsMonthlyThemeLink(),
        memberDropInSessionsTalksLink(),
        memberDropInSessionsMeditationsDiscussionsLink(),
        memberDropInSessionsSilentMeditationsLink(),
        memberDropInSessionsWelcomeSessionsLink(),
        memberDropInSessionsAllSessionsLink(),
    ],
});

const getSanghaLink = (sangha: Sangha) => ({
    name: sangha.name,
    to: routes.memberSanghaDetails(sangha.id),
    tags: [],
});

const getSanghaLinks = (sanghas: Sangha[], type: SanghaType) =>
    sanghas.filter((x) => x.type === type).map(getSanghaLink);

const memberMindfulFriendsLink = (sanghas: Sangha[]): MenuLinkInfo => {
    const subNavLinks: MenuLinkInfo[] = getSanghaLinks(sanghas, SanghaType.InterestGroup);
    if (subNavLinks.length > 0) {
        subNavLinks.push({
            name: "Mindful Friends Groups",
            testId: "mindfulFriendsSubNavLink",
            to: routes.memberMindfulFriends(),
            tags: [],
        });
    }

    return {
        name: "Mindful Friends",
        testId: "mindfulFriendsNavLink",
        to: routes.memberMindfulFriends(),
        tags: [],
        subNavLinks,
    };
};

const memberContactSupportLink = (): MenuLinkInfo => ({
    name: "Contact Support",
    to: routes.contactUs(),
    tags: [LinkTag.ACCOUNT_LINKS],
});

const memberHelpLink = (): MenuLinkInfo => ({
    ...faqLink(),
    tags: [LinkTag.ACCOUNT_LINKS],
});

const sanghaLink = (sanghas: Sangha[], isImmersion: boolean): MenuLinkInfo => {
    return {
        name: "Mentorship Groups",
        to: routes.memberSanghas(),
        testId: "mySanghaNavLink",
        tags: [],
        subNavLinks: getSanghaLinks(sanghas, SanghaType.MentorshipGroup),
        indicatorKey:
            isImmersion || !isFeatureEnabled(SiteFeature.Indicator)
                ? undefined
                : "mentorshipGroupIndicator",
    };
};

const memberGuidelinesLink = (): MenuLinkInfo => ({
    name: "Community Guidelines",
    to: routes.communityGuidelines(),
    tags: [],
});

const memberTeachersPage = (): MenuLinkInfo => ({
    name: "Teachers",
    testId: "teachersPageNavLink",
    to: routes.memberTeachersPage(),
    tags: [],
});

const conflictResolutionLink = (): MenuLinkInfo => ({
    name: "Navigating Issues with Care",
    to: routes.conflictResolution(),
    tags: [],
});

const payItForwardLink = (): MenuLinkInfo => ({
    name: "Pay It Forward",
    to: routes.memberContribute(),
    testId: "payItForwardNavLink",
    tags: [],
});

const resourcesLink = (): MenuLinkInfo => {
    return {
        name: "Resources",
        to: "#",
        testId: "resourcesNavLink",
        tags: [],
        subNavLinks: [
            memberTeachersPage(),
            contentLibraryLink(),
            memberGuidelinesLink(),
            memberHelpLink(),
            memberContactSupportLink(),
            conflictResolutionLink(),
            payItForwardLink(),
        ],
    };
};

const teacherPortalLink = (): MenuLinkInfo => ({
    name: "Teacher Portal",
    to: TEACHER_PORTAL_LINK,
    external: true,
    tags: [LinkTag.ACCOUNT_LINKS],
});

const webflowTeachersLink = (): MenuLinkInfo => ({
    name: "Teachers",
    to: routes.teacherSection(),
    tags: [],
});

const webflowTestimonialsLink = (): MenuLinkInfo => ({
    name: "Testimonials",
    to: routes.testimonials(),
    tags: [],
});

const loginLink = (): MenuLinkInfo => ({
    name: "Login",
    to: routes.memberZone(),
    testId: "loginNavLink",
    tags: [],
});

/**
 * It returns an array of objects that describe the links to be displayed in the navigation bar
 * @param {boolean} [isFullPageMenu] - used for the switch group option in the navbar.
 * @returns An array of MenuLinkInfo objects.
 */
export const useNavigationLinks = (isFullPageMenu?: boolean): MenuLinkInfo[] => {
    const location = useLocation();
    const isLoggedIn = useSelector(selectIsLoggedIn);
    const isTeacher = useSelector(selectIsTeacher);
    const canAccessMemberZone = useSelector(selectCanAccessMemberZone);
    const user = useSelector(selectUser);
    const isImmersion = user?.features?.includes(ProductFeature.DEDICATED_SANGHA) ?? false;

    const {
        data: membershipData,
        loading: membershipsAreLoading,
        error: membershipsError,
    } = useQuery(GRAPHQL_QUERY_MEMBERSHIPS);

    const memberships = (membershipData?.myMemberships || []) as Membership[];
    const activeMemberships = memberships.filter((m) => MembershipStatus.isActive(m.status));
    const membership = activeMemberships[0];

    const isFreeTrial = membership && membershipUsesFreeTrialInternally(membership);

    const logInOutButtonObj = logInOutButton(isLoggedIn);

    const initialLinks: MenuLinkInfo[] = [];

    if (isFullPageMenu) {
        initialLinks.push(homeLink());
    }

    const [loadSanghas, sanghaQueryStatus] = useLazyQuery(GRAPHQL_QUERY_MY_SANGHAS, {
        fetchPolicy: "network-only",
    });
    const sanghas = (sanghaQueryStatus.data?.mySanghas || []) as Sangha[];
    useEffect(() => {
        if (isLoggedIn && canAccessMemberZone) {
            loadSanghas();
        }
    }, [isLoggedIn, canAccessMemberZone, user?.id]);

    if (isLoggedIn && canAccessMemberZone) {
        // For pending/cancelled membership notices, we just want to show the logout option
        if (isMemberZoneRoot(location.pathname)) {
            return [logInOutButtonObj];
        }

        initialLinks.push(
            memberMindfulFriendsLink(sanghas),
            memberDropInSessionsLink(),
            ...(isFreeTrial ? [] : [sanghaLink(sanghas, isImmersion)]),
            resourcesLink(),
        );

        if (isTeacher) {
            return [...initialLinks, accountLink(), teacherPortalLink(), logInOutButtonObj];
        } else {
            return [...initialLinks, accountLink(), logInOutButtonObj];
        }
    }

    // default visitor links
    if (isFullPageMenu) {
        return [
            homeLink(),
            visitorDonateLink(),
            aboutUsLink(),
            contactUsLink(),
            faqLink(),
            logInOutButtonObj,
        ];
    }

    return [
        // ...initialLinks,

        webflowTestimonialsLink(),
        webflowTeachersLink(),
        aboutUsLink(),
        visitorDonateLink(),
        isLoggedIn ? logInOutButtonObj : loginLink(),
    ];
};
